import { useEffect, useState } from "react";
import {
  CalendarClock, Plus, Copy, Trash2, Pencil, CheckCircle2, RotateCcw, Loader2, List, CalendarDays, Send,
} from "lucide-react";
import { CalendarGrid } from "@/components/admin/CalendarGrid";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const PLATFORMS = ["Facebook", "Instagram", "X", "LinkedIn"];
const PLATFORM_STYLE = {
  Facebook: "bg-[#1877F2]/10 text-[#1877F2]",
  Instagram: "bg-pink-100 text-pink-600",
  X: "bg-brand-navy/10 text-brand-navy",
  LinkedIn: "bg-[#0A66C2]/10 text-[#0A66C2]",
};
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600",
  scheduled: "bg-amber-100 text-amber-700",
  posted: "bg-emerald-100 text-emerald-700",
  publishing: "bg-blue-100 text-blue-700",
  failed: "bg-red-100 text-red-700",
};

const fieldCls = "rounded-xl border-brand-line focus-visible:ring-brand-red/30 focus-visible:border-brand-navy";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
const fmtWhen = (iso) => {
  if (!iso) return "No date set";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) +
    " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

function PostDialog({ open, onOpenChange, initial, onSave }) {
  const [platform, setPlatform] = useState(initial?.platform || "Facebook");
  const [content, setContent] = useState(initial?.content || "");
  const [hashtags, setHashtags] = useState((initial?.hashtags || []).join(" "));
  const [when, setWhen] = useState(toLocalInput(initial?.scheduled_at));
  const [status, setStatus] = useState(initial?.status === "failed" ? "scheduled" : initial?.status || "scheduled");
  const [imageUrl, setImageUrl] = useState(initial?.image_url || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!content.trim()) { toast.error("Add some post content first."); return; }
    setSaving(true);
    await onSave({
      platform,
      content: content.trim(),
      hashtags: hashtags.split(/\s+/).map((h) => h.trim()).filter(Boolean),
      scheduled_at: fromLocalInput(when),
      status,
      image_url: imageUrl.trim() || null,
    });
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="post-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-navy">{initial?.id ? "Edit post" : "Schedule a post"}</DialogTitle>
          <DialogDescription className="text-brand-ink2">Plan your content. Publishing to channels activates once your social accounts are connected.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-brand-navy font-semibold">Platform</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="post-platform" className={`h-11 ${fieldCls}`}><SelectValue /></SelectTrigger>
                <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-brand-navy font-semibold">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="post-status" className={`h-11 ${fieldCls}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="posted">Posted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-brand-navy font-semibold">Content</Label>
            <Textarea data-testid="post-content" value={content} onChange={(e) => setContent(e.target.value)}
              placeholder="What do you want to post?" className={`min-h-[100px] ${fieldCls}`} />
          </div>
          <div className="space-y-2">
            <Label className="text-brand-navy font-semibold">Hashtags (space-separated)</Label>
            <Input data-testid="post-hashtags" value={hashtags} onChange={(e) => setHashtags(e.target.value)}
              placeholder="#MedicalAlert #SeniorSafety" className={`h-11 ${fieldCls}`} />
          </div>
          <div className="space-y-2">
            <Label className="text-brand-navy font-semibold">Schedule for</Label>
            <Input data-testid="post-datetime" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
              className={`h-11 ${fieldCls}`} />
          </div>
          <div className="space-y-2">
            <Label className="text-brand-navy font-semibold">
              Image URL {platform === "Instagram"
                ? <span className="text-brand-red">(required for Instagram publishing)</span>
                : <span className="font-normal text-brand-ink2">(optional)</span>}
            </Label>
            <Input data-testid="post-image-url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://… direct public JPEG link" className={`h-11 ${fieldCls}`} />
          </div>
        </div>
        <DialogFooter>
          <button onClick={save} disabled={saving} data-testid="post-save"
            className="btn-focus inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save post"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ContentCalendar({ posts, onCreate, onUpdate, onDelete, onPublishNow }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("list");
  const [autoPub, setAutoPub] = useState({ enabled: false, social_connected: false });
  const [autoPubLoaded, setAutoPubLoaded] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  useEffect(() => {
    api.get("/admin/scheduled-posts/auto-publish")
      .then(({ data }) => setAutoPub((s) => (autoPubLoaded ? s : data)))
      .catch(() => {})
      .finally(() => setAutoPubLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAutoPub = async (enabled) => {
    setAutoPub((s) => ({ ...s, enabled }));
    try {
      await api.post("/admin/scheduled-posts/auto-publish", { enabled });
      toast.success(enabled ? "Auto-publish turned on — due posts go out within a minute" : "Auto-publish turned off");
    } catch (err) {
      setAutoPub((s) => ({ ...s, enabled: !enabled }));
      toast.error(formatApiError(err.response?.data?.detail, "Could not update auto-publish."));
    }
  };

  const publishNow = async (id) => {
    setPublishingId(id);
    await onPublishNow(id);
    setPublishingId(null);
  };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openNewOn = (date) => { setEditing({ scheduled_at: date.toISOString() }); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setDialogOpen(true); };

  const handleSave = async (payload) => {
    if (editing?.id) await onUpdate(editing.id, payload);
    else await onCreate(payload);
    setDialogOpen(false);
  };

  const copyPost = (p) => {
    navigator.clipboard?.writeText(`${p.content}\n\n${(p.hashtags || []).join(" ")}`).then(
      () => toast.success("Post copied"),
      () => toast.error("Could not copy"),
    );
  };

  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5 shadow-card" data-testid="content-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-navy"><CalendarClock className="w-5 h-5 text-white" strokeWidth={2.2} /></span>
          <div>
            <h2 className="font-display font-bold text-lg text-brand-navy leading-tight">Content calendar</h2>
            <p className="text-sm text-brand-ink2" data-testid="auto-publish-status-text">
              {autoPub.enabled
                ? autoPub.social_connected
                  ? "Auto-publish is ON — scheduled Facebook & Instagram posts go out automatically."
                  : "Auto-publish is ON, but no social account is connected yet — connect your Facebook Page above."
                : "Queue & schedule posts. Turn on auto-publish to post to Facebook & Instagram automatically."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 rounded-full border border-brand-line px-3.5 h-11 cursor-pointer" data-testid="auto-publish-control">
            <span className="text-sm font-bold text-brand-navy">Auto-publish</span>
            <Switch checked={autoPub.enabled} onCheckedChange={toggleAutoPub} disabled={!autoPubLoaded} data-testid="auto-publish-toggle" />
          </label>
          <div className="inline-flex rounded-full border border-brand-line p-1" data-testid="calendar-view-toggle">
            <button onClick={() => setView("list")} data-testid="calendar-view-list"
              className={`btn-focus inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-bold transition-colors ${view === "list" ? "bg-brand-navy text-white" : "text-brand-navy hover:bg-brand-bg"}`}>
              <List className="w-4 h-4" /> List
            </button>
            <button onClick={() => setView("month")} data-testid="calendar-view-month"
              className={`btn-focus inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-bold transition-colors ${view === "month" ? "bg-brand-navy text-white" : "text-brand-navy hover:bg-brand-bg"}`}>
              <CalendarDays className="w-4 h-4" /> Month
            </button>
          </div>
          <button onClick={openNew} data-testid="content-add-post"
            className="btn-focus inline-flex items-center gap-2 h-11 px-5 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors">
            <Plus className="w-4 h-4" /> New post
          </button>
        </div>
      </div>

      {view === "month" ? (
        <CalendarGrid posts={posts} onDayAdd={openNewOn} onEdit={openEdit} />
      ) : posts.length === 0 ? (
        <div className="h-32 grid place-items-center text-center text-brand-ink2 text-sm px-4" data-testid="content-calendar-empty">
          No scheduled posts yet. Add one, or click <span className="font-bold text-brand-navy mx-1">Schedule</span> on any AI-generated social post.
        </div>
      ) : (
        <div className="space-y-3" data-testid="content-calendar-list">
          {posts.map((p) => (
            <div key={p.id} data-testid={`scheduled-post-${p.id}`} className="rounded-xl border border-brand-line p-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${PLATFORM_STYLE[p.platform] || "bg-slate-100 text-slate-600"}`}>{p.platform}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${STATUS_STYLE[p.status] || "bg-slate-100 text-slate-600"}`}>{p.status}</span>
                <span className="text-xs font-semibold text-brand-ink2 ml-auto">{fmtWhen(p.scheduled_at)}</span>
              </div>
              <p className="text-brand-navy text-sm whitespace-pre-wrap leading-relaxed">{p.content}</p>
              {(p.hashtags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {p.hashtags.map((h, j) => <span key={j} className="text-xs font-semibold text-brand-red bg-brand-red/8 px-2 py-0.5 rounded-full">{h}</span>)}
                </div>
              )}
              {p.status === "failed" && p.publish_error && (
                <p className="mt-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid={`post-error-${p.id}`}>
                  {p.publish_error}
                </p>
              )}
              <div className="flex items-center gap-1.5 mt-3">
                {(p.platform === "Facebook" || p.platform === "Instagram") && p.status !== "posted" && p.status !== "publishing" && onPublishNow && (
                  <button onClick={() => publishNow(p.id)} disabled={publishingId === p.id} data-testid={`post-publish-${p.id}`}
                    className="btn-focus inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy-700 transition-colors disabled:opacity-60">
                    {publishingId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Publish now
                  </button>
                )}
                <button onClick={() => copyPost(p)} data-testid={`post-copy-${p.id}`} className="btn-focus inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-brand-line text-sm font-semibold text-brand-navy hover:bg-brand-bg transition-colors"><Copy className="w-3.5 h-3.5" /> Copy</button>
                {p.status === "posted" ? (
                  <button onClick={() => onUpdate(p.id, { status: "scheduled" })} data-testid={`post-unpost-${p.id}`} className="btn-focus inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-brand-line text-sm font-semibold text-brand-navy hover:bg-brand-bg transition-colors"><RotateCcw className="w-3.5 h-3.5" /> Reschedule</button>
                ) : (
                  <button onClick={() => onUpdate(p.id, { status: "posted" })} data-testid={`post-markposted-${p.id}`} className="btn-focus inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-brand-line text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"><CheckCircle2 className="w-3.5 h-3.5" /> Mark posted</button>
                )}
                <button onClick={() => openEdit(p)} data-testid={`post-edit-${p.id}`} className="btn-focus grid place-items-center w-9 h-9 rounded-full border border-brand-line text-brand-navy hover:bg-brand-bg transition-colors ml-auto"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => onDelete(p.id)} data-testid={`post-delete-${p.id}`} className="btn-focus grid place-items-center w-9 h-9 rounded-full border border-brand-line text-brand-red hover:bg-brand-red/8 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {dialogOpen && (
        <PostDialog
          key={editing?.id || "new"}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          initial={editing}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
